import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CircularProgress,
  Container,
  Typography,
  Box,
  Card,
} from "@mui/material";
import { forgot, verifyOtp } from "../../service/authService";
import AppButton from "../../components/AppButton";
import { useSnackbar } from "../../App"; // Import useSnackbar hook

const Otp = () => {
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar(); // Use the snackbar hook

  const [resendCooldown, setResendCooldown] = useState(0); // seconds left
  const [resendLoading, setResendLoading] = useState(false);

  const handleChange = (element, index) => {
    const value = element.value.replace(/[^0-9]/g, "");
    if (!value) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (element.nextSibling) {
      element.nextSibling.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      if (otp[index]) {
        const newOtp = [...otp];
        newOtp[index] = "";
        setOtp(newOtp);
      } else if (e.target.previousSibling) {
        e.target.previousSibling.focus();
      }
    } else if (e.key === "ArrowLeft" && e.target.previousSibling) {
      e.target.previousSibling.focus();
    } else if (e.key === "ArrowRight" && e.target.nextSibling) {
      e.target.nextSibling.focus();
    }
  };

  const handleClear = () => {
    setOtp(new Array(6).fill(""));
  };

  const handleSubmit = async (e) => {
  e.preventDefault();

  const email = localStorage.getItem("email");
  if (!email) {
    showSnackbar("Email is not set. Please go back and try again.", "error");
    return;
  }

  const joinedOtp = otp.join("");
  if (joinedOtp.length !== 6) {
    showSnackbar("Please enter a complete 6-digit OTP", "error");
    return;
  }

  setLoading(true);

  const startTime = Date.now();

  try {
    await verifyOtp(email, joinedOtp);

    const elapsed = Date.now() - startTime;
    const minimumDelay = 2000;
    const remainingDelay = minimumDelay - elapsed;

    if (remainingDelay > 0) {
      await new Promise((resolve) => setTimeout(resolve, remainingDelay));
    }

    localStorage.setItem("otp", joinedOtp);
    showSnackbar("OTP verified successfully", "success");
    navigate("/confirm");
  } catch (error) {
    const message = error?.response?.data?.message || "Failed to verify OTP";
    showSnackbar(message, "error");
    handleClear();

    const elapsed = Date.now() - startTime;
    const remainingDelay = 2000 - elapsed;
    if (remainingDelay > 0) {
      await new Promise((resolve) => setTimeout(resolve, remainingDelay));
    }
  } finally {
    setLoading(false);
  }
};


const handleResend = async () => {
  const email = localStorage.getItem("email");

  if (!email) {
    showSnackbar("Email not found. Please try again.", "error");
    return;
  }

  // If already in cooldown, don't allow resend
  if (resendCooldown > 0) {
    showSnackbar(`Please wait ${resendCooldown} seconds before resending.`, "info");
    return;
  }

  setResendLoading(true);

  try {
    await forgot(email);

    showSnackbar("New OTP sent successfully!", "success");
    
    setResendCooldown(300);

    handleClear();
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      "Failed to resend OTP. Please try again.";
    showSnackbar(message, "error");
  } finally {
    setResendLoading(false);
  }
};

useEffect(() => {
  let timer;
  if (resendCooldown > 0) {
    timer = setTimeout(() => {
      setResendCooldown(resendCooldown - 1);
    }, 1000);
  }
  return () => clearTimeout(timer);
}, [resendCooldown]);

  return (
    <Container
      maxWidth="sm"
      sx={{
        display: "flex",
        minHeight: "100vh",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        borderRadius: "12px",
      }}
    >
      <Card
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          mt: { xs: 4, md: 10 },
          width: { xs: "100%", sm: "80%", md: "70%" },
          margin: "0 auto",
          mb: 4,
          p: { xs: 2, sm: 4 },
          boxShadow: "0px 4px 20px 4px rgba(0, 0, 0, 0.1)",
          fontFamily: "Poppins, sans-serif",
          borderRadius: "12px",
        }}
      >
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            fontFamily: "Poppins, sans-serif",
            mb: { xs: 1, sm: 4 },
            fontSize: { xs: "1.25rem", sm: "1.5rem" },
          }}
        >
          Enter OTP
        </Typography>

        <Box component="form" onSubmit={handleSubmit} sx={{ width: "100%" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              gap: { xs: 1, sm: 2 },
              mb: { xs: 2, sm: 1 },
              flexWrap: "wrap",
            }}
          >
            {otp.map((digit, index) => (
              <input
                key={index}
                type="text"
                inputMode="numeric"
                aria-label={`OTP digit ${index + 1}`}
                maxLength="1"
                value={digit}
                onChange={(e) => handleChange(e.target, index)}
                onKeyDown={(e) => handleKeyDown(e, index)}
                autoFocus={index === 0}
                style={{
                  width: window.innerWidth < 400 ? "32px" : "40px",
                  height: window.innerWidth < 400 ? "32px" : "40px",
                  fontSize: window.innerWidth < 400 ? "18px" : "24px",
                  textAlign: "center",
                  border: "1px solid #ddd",
                  borderRadius: "8px",
                  outline: "none",
                  fontFamily: "Poppins, sans-serif",
                  transition: "border-color 0.3s",
                }}
              />
            ))}
          </Box>

          <AppButton
            fullWidth
            type="submit"
            color="primary"
            disabled={loading}
            sx={{ mt: { xs: 2, sm: 3 }, fontFamily: "Poppins, sans-serif" }}
          >
            {loading ? <CircularProgress size={24} /> : "Verify OTP"}
          </AppButton>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: { xs: 2, sm: 3 },
              gap: { xs: 2, sm: 4 },
              fontFamily: "Poppins, sans-serif",
              flexWrap: "wrap",
            }}
          >
           <Typography
  variant="body2"
  onClick={handleResend}
  sx={{
    cursor: resendLoading || resendCooldown > 0 ? "not-allowed" : "pointer",
    opacity: resendLoading || resendCooldown > 0 ? 0.6 : 1,
    color: resendCooldown > 0 ? "#999" : "inherit",
    transition: "all 0.3s ease-in-out",
    "&:hover": {
      color: resendLoading || resendCooldown > 0 ? "#999" : "#7C0BCC",
    },
  }}
>
  {resendLoading
    ? "Sending..."
    : resendCooldown > 0
    ? `Resend OTP? (${Math.floor(resendCooldown / 60)}:${String(resendCooldown % 60).padStart(2, "0")})`
    : "Resend OTP?"}
</Typography>

            <Link
              to="/forgot"
              style={{
                textDecoration: "none",
                color: "inherit",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  cursor: "pointer",
                  transition: "color 0.3s ease-in-out",
                  "&:hover": { color: "#FF0000" },
                }}
              >
                Cancel
              </Typography>
            </Link>
          </Box>
        </Box>
      </Card>
    </Container>
  );
};

export default Otp;
